import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import CircularProgress from '@/components/common/CircularProgress';
import { MetricCard } from './MetricCard';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { usePsychologistContext } from '@/contexts/PsychologistContext';
import apiCoreAdapter from '@/hooks/apiCoreAdapter';
import type { PsychologistMetrics } from '@/hooks/api/psychologist/metrics';
import { PsiPlusChatbot } from './PsiPlusChatbot';

interface Props {
  metrics: PsychologistMetrics | null;
  loading: boolean;
  onNavigateToInactividad?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateForApi(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatSessionDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return dateStr; }
}

function getCurrentQuarter() {
  const now = new Date();
  const month = now.getMonth();
  const q = Math.floor(month / 3);
  const LABELS = ['Q1', 'Q2', 'Q3', 'Q4'];
  const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const ORDINALS = ['primer', 'segundo', 'tercer', 'cuarto'];
  const startMonth = q * 3;
  const endMonth = startMonth + 2;
  const year = now.getFullYear();
  return {
    label: `${LABELS[q]} ${year} — ${MONTHS[startMonth]} a ${MONTHS[endMonth]}`,
    detailLabel: `Detalle ${MONTHS[startMonth]} - ${MONTHS[endMonth]}, ${ORDINALS[q]} trimestre ${year}`,
    start: new Date(year, startMonth, 1, 0, 0, 0),
    end: new Date(year, endMonth + 1, 0, 23, 59, 59),
  };
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface SessionDetail { id: string; date: string; }

interface ProcessProgress {
  processId: string;
  patientName: string;
  therapyType: string;
  totalSessions: number;
  quarterSessions: number;
  quarterDetails: SessionDetail[];
  allTimeDetails: SessionDetail[];
  eligibleInQ: number;
  eligibleDetails: SessionDetail[];
  eligibleDate10: string | null;
  eligible5InQ: number;
  eligible5Details: SessionDetail[];
  eligibleDate5: string | null;
}

type TierId = 'gold' | 'silver' | 'bronze';
type ViewMode = 'process' | 'date';
type SubTab = 'detalles' | 'bono-500' | 'bonos-1000-2000';
type PanelId = 'hub' | 'tasa-inactividad' | 'procesos-terapeuticos' | 'procesos-elegibles' | 'bloqueos';

const TIER_CONFIG = {
  gold: { id: 'gold' as TierId, emoji: '🥇', bonus: '$2.000', minSessions: 250, universe: '10+' },
  silver: { id: 'silver' as TierId, emoji: '🥈', bonus: '$1.000', minSessions: 150, universe: '10+' },
  bronze: { id: 'bronze' as TierId, emoji: '🥉', bonus: '$500', minSessions: 150, universe: '5+' },
};

const PANEL_TITLES: Record<Exclude<PanelId, 'hub'>, string> = {
  'tasa-inactividad': 'Tasa de inactividad',
  'procesos-terapeuticos': '¿Qué es un proceso terapéutico?',
  'procesos-elegibles': 'Procesos y sesiones elegibles',
  'bloqueos': 'Bloqueos de agenda',
};

const PANEL_DESCRIPTIONS: Record<Exclude<PanelId, 'hub'>, string> = {
  'tasa-inactividad': 'Qué mide y por qué es la métrica más importante.',
  'procesos-terapeuticos': 'Qué es un proceso terapéutico y sus estados posibles.',
  'procesos-elegibles': 'Cómo funcionan los umbrales de 5 y 10 sesiones y ejemplos prácticos.',
  'bloqueos': 'Tipos de bloqueo y cómo afectan tus asignaciones indirectamente.',
};

const HUB_PANELS: Exclude<PanelId, 'hub'>[] = [
  'tasa-inactividad',
  'procesos-terapeuticos',
  'procesos-elegibles',
  'bloqueos',
];

// ─── Concept panel (portal) ───────────────────────────────────────────────────

interface ConceptPanelProps {
  panelStack: PanelId[];
  highlightSection?: 'universe-5' | 'universe-10' | null;
  onClose: () => void;
  onNavigate: (id: PanelId) => void;
  onBack: () => void;
  onNavigateToInactividad?: () => void;
}

const ConceptPanel: React.FC<ConceptPanelProps> = ({
  panelStack, highlightSection, onClose, onNavigate, onBack, onNavigateToInactividad,
}) => {
  const isOpen = panelStack.length > 0;
  const currentView = panelStack[panelStack.length - 1] ?? null;
  const canGoBack = panelStack.length > 1;

  useEffect(() => {
    if (!highlightSection || currentView !== 'procesos-elegibles') return;
    const el = document.getElementById(highlightSection);
    if (!el) return;
    const timer = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      let flashes = 0;
      const flash = () => {
        el.style.outline = '2px solid #f59e0b';
        el.style.borderRadius = '10px';
        setTimeout(() => {
          el.style.outline = '';
          el.style.borderRadius = '';
          flashes++;
          if (flashes < 3) setTimeout(flash, 300);
        }, 350);
      };
      setTimeout(flash, 300);
    }, 200);
    return () => clearTimeout(timer);
  }, [highlightSection, currentView]);

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black/30 z-[9998] transition-opacity duration-200 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`fixed right-0 top-0 h-full w-[440px] max-w-full bg-white shadow-2xl z-[9999] flex flex-col transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {canGoBack && (
              <button
                type="button"
                onClick={onBack}
                className="text-[#665090] hover:text-[#4A3D73] transition-colors shrink-0"
                aria-label="Volver"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            <span className="text-sm font-semibold text-[#2D2049] leading-snug truncate">
              {currentView === 'hub'
                ? 'Repasemos conceptos'
                : currentView
                  ? PANEL_TITLES[currentView as Exclude<PanelId, 'hub'>]
                  : ''}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
            aria-label="Cerrar panel"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">

          {currentView === 'hub' && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-gray-500 mb-1">Tocá un concepto para ver la explicación detallada.</p>
              {HUB_PANELS.map(key => (
                <button
                  key={key}
                  type="button"
                  onClick={() => onNavigate(key)}
                  className="w-full text-left bg-white border border-gray-100 rounded-2xl px-5 py-4 flex items-center justify-between gap-4 hover:border-[#C4B5FD] hover:shadow-sm transition-all"
                >
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-[#2D2049]">{PANEL_TITLES[key]}</span>
                    <span className="text-xs text-gray-400 leading-snug">{PANEL_DESCRIPTIONS[key]}</span>
                  </div>
                  <ChevronRight size={18} className="text-[#665090] shrink-0" />
                </button>
              ))}
            </div>
          )}

          {currentView === 'tasa-inactividad' && (
            <div className="flex flex-col gap-6">
              <p className="text-sm text-gray-600 leading-relaxed">
                La tasa de inactividad mide qué porcentaje de tus procesos activos pasan a
                inactivo cada mes. Un proceso pasa a inactivo cuando transcurren{' '}
                <strong>30 días sin que haya una sesión</strong>.
              </p>
              <div className="flex flex-col gap-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                  Por qué importa tanto
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Es la métrica que más influye en cuántos pacientes nuevos podemos asignarte.
                  El sistema la revisa mes a mes y ajusta tu límite de asignaciones en base a
                  ella.
                </p>
              </div>
              <div className="bg-[#ECFFD5] rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-[#2D2049] leading-relaxed">
                  La condición base: mantenerla por debajo del <strong>10% mensual</strong> de
                  forma sostenida.
                </p>
              </div>
              {onNavigateToInactividad && (
                <button
                  type="button"
                  onClick={() => { onClose(); onNavigateToInactividad(); }}
                  className="flex items-center gap-2 bg-violet-50 border border-violet-200 text-violet-700 text-sm font-semibold px-4 py-3 rounded-xl hover:bg-violet-100 transition-colors self-start"
                >
                  Ver mi tasa de inactividad →
                </button>
              )}
            </div>
          )}

          {currentView === 'procesos-terapeuticos' && (
            <div className="flex flex-col gap-5">
              <p className="text-sm text-gray-600 leading-relaxed">
                Cada vez que empezás a atender a un paciente con un tipo de terapia, se abre un <strong>proceso terapéutico</strong>. Es la relación terapéutica entre vos y ese paciente — la combinación de psicóloga + paciente + tipo de terapia (individual, pareja, familia, etc).
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Un proceso acumula sesiones a lo largo del tiempo. A partir de la <strong>sesión número 5</strong> de cada proceso con 5+ sesiones, o a partir de la <strong>sesión número 10</strong> de cada proceso con 10+ sesiones, las sesiones empiezan a contar como <strong>sesiones elegibles</strong> para los bonos trimestrales correspondientes.
              </p>
              <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                <p className="text-sm text-violet-800 leading-relaxed">
                  Cuanto más activos se mantienen tus procesos, más sesiones elegibles acumulás en el trimestre — y más chance tenés de acceder al bono más alto.
                </p>
              </div>
            </div>
          )}

          {currentView === 'procesos-elegibles' && (
            <div className="flex flex-col gap-5">
              <p className="text-sm text-gray-600 leading-relaxed">
                Hay <strong>dos universos independientes</strong> de sesiones elegibles, cada uno con su propio umbral mínimo y su propio bono asociado. Un proceso puede pertenecer a uno o ambos universos dependiendo de cuántas sesiones acumuladas tenga.
              </p>

              <div id="universe-5" className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🥉</span>
                  <p className="text-sm font-bold text-amber-700">Universo 5+ — bono de $500</p>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Procesos con <strong>5 o más sesiones acumuladas</strong>. A partir de la sesión número 5, cada sesión se contabiliza como sesión elegible para el bono de $500.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex flex-col gap-2">
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">Ejemplo: el paciente Pedro</p>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    Pedro empieza su proceso. Cinco semanas después llega a su <strong>sesión número 5</strong>.
                  </p>
                  <div className="flex flex-col gap-2 mt-1">
                    {[
                      { sessions: 'Sesiones 1 a 4', note: 'No generan sesiones elegibles todavía.', isEligible: false },
                      { sessions: 'Sesión 5', note: 'Primera sesión elegible para el bono de $500.', isEligible: true },
                      { sessions: 'Sesiones 6, 7, 8…', note: 'Cada una suma una sesión elegible más. Cuentan en el trimestre en que ocurran.', isEligible: true },
                    ].map((row, i) => (
                      <div key={i} className={`flex gap-3 items-start p-2.5 rounded-lg ${row.isEligible ? 'bg-amber-100' : 'bg-gray-50'}`}>
                        <span className={`text-xs font-bold shrink-0 w-28 pt-0.5 ${row.isEligible ? 'text-amber-700' : 'text-gray-400'}`}>
                          {row.sessions}
                        </span>
                        <span className={`text-xs leading-relaxed ${row.isEligible ? 'text-amber-800' : 'text-gray-500'}`}>
                          {row.note}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <hr className="border-gray-200" />

              <div id="universe-10" className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🥈🥇</span>
                  <p className="text-sm font-bold text-sky-700">Universo 10+ — bonos de $1.000 y $2.000</p>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Procesos con <strong>10 o más sesiones acumuladas</strong>. A partir de la sesión número 10, cada sesión se contabiliza como sesión elegible para los bonos de $1.000 y $2.000.
                </p>
                <div className="bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 flex flex-col gap-2">
                  <p className="text-xs font-bold text-sky-700 uppercase tracking-wide">Ejemplo: la paciente Paula</p>
                  <p className="text-sm text-sky-800 leading-relaxed">
                    Paula empieza su proceso el <strong>10 de junio de 2026</strong>. Diez semanas después llega a su <strong>sesión número 10</strong>.
                  </p>
                  <div className="flex flex-col gap-2 mt-1">
                    {[
                      { sessions: 'Sesiones 1 a 9', note: 'No generan sesiones elegibles para los bonos de $1.000/$2.000.', isEligible: false },
                      { sessions: 'Sesión 10', note: 'Primera sesión elegible. Cuenta como "sesión elegible 1" para los bonos de $1.000 y $2.000.', isEligible: true },
                      { sessions: 'Sesiones 11, 12, 13…', note: 'Cada una suma una sesión elegible más. Cuentan en el trimestre en que ocurran.', isEligible: true },
                    ].map((row, i) => (
                      <div key={i} className={`flex gap-3 items-start p-2.5 rounded-lg ${row.isEligible ? 'bg-sky-100' : 'bg-gray-50'}`}>
                        <span className={`text-xs font-bold shrink-0 w-28 pt-0.5 ${row.isEligible ? 'text-sky-700' : 'text-gray-400'}`}>
                          {row.sessions}
                        </span>
                        <span className={`text-xs leading-relaxed ${row.isEligible ? 'text-sky-800' : 'text-gray-500'}`}>
                          {row.note}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <p className="text-sm text-amber-800 leading-relaxed">
                  <strong>Nota:</strong> Las sesiones elegibles no se acumulan entre trimestres. Lo que cuenta es cuántas sesiones elegibles ocurren dentro de cada período de 3 meses.
                </p>
              </div>
            </div>
          )}

          {currentView === 'bloqueos' && (
            <div className="flex flex-col gap-6">
              <p className="text-sm text-gray-600 leading-relaxed">
                Llamamos bloqueo a la decisión de dejar de asignar nuevos consultantes durante
                determinada cantidad de tiempo. Esto no afecta de ninguna forma a los procesos
                que ya atendés de manera activa — simplemente, durante el período de bloqueo, no
                recibís procesos nuevos.
              </p>
              <div className="flex flex-col divide-y divide-gray-100">
                {[
                  { name: 'No cuenta con horarios de reprogramación', description: 'Si necesitás habilitar más horarios en tu agenda.' },
                  { name: 'Demasiados pacientes', description: 'Si llegaste al límite de procesos permitidos.' },
                  { name: 'Límite de pacientes dentro de un período', description: 'Si se te asignaron muchos procesos en poco tiempo (específicamente 5 en un período de 30 días).' },
                  { name: 'Tope de pacientes (solicitado por psico)', description: 'Si vos nos pediste explícitamente atender a una menor cantidad de procesos que nuestro máximo, y llegaste a ese número.' },
                  { name: 'Situaciones personales extraordinarias', description: 'Si nos comentaste que estabas atravesando una situación personal y que por eso necesitabas una pausa.' },
                  { name: 'Vacaciones y días off', description: 'Si estás en un período importante de días off.' },
                ].map(block => (
                  <div key={block.name} className="py-4 flex flex-col gap-1.5 first:pt-0 last:pb-0">
                    <span className="text-sm font-semibold text-[#4A3D73]">{block.name}</span>
                    <span className="text-sm text-gray-500 leading-relaxed">{block.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </>,
    document.body,
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

export const MetricsPsiPlusTab: React.FC<Props> = ({ metrics: _metrics, loading: _metricsLoading, onNavigateToInactividad }) => {
  const { psychologist } = usePsychologistContext();
  const [progressData, setProgressData] = useState<ProcessProgress[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [openProcesses, setOpenProcesses] = useState<Set<string>>(new Set());
  const [openMaximizar, setOpenMaximizar] = useState(false);
  const [openNotEligible5Tab2, setOpenNotEligible5Tab2] = useState(false);
  const [openNotEligible10Tab3, setOpenNotEligible10Tab3] = useState(false);
  const [expandedNE5, setExpandedNE5] = useState<Set<string>>(new Set());
  const [expandedNE10, setExpandedNE10] = useState<Set<string>>(new Set());
  const [expandedNE5Outside, setExpandedNE5Outside] = useState<Set<string>>(new Set());
  const [expandedNE10Outside, setExpandedNE10Outside] = useState<Set<string>>(new Set());
  const [panelStack, setPanelStack] = useState<PanelId[]>([]);
  const [panelHighlight, setPanelHighlight] = useState<'universe-5' | 'universe-10' | null>(null);
  const [showAssistant, setShowAssistant] = useState(false);
  const [viewModes, setViewModes] = useState<Record<string, ViewMode>>({});
  const [subTab, setSubTab] = useState<SubTab>('detalles');

  const navigate = useNavigate();
  const isNueva = psychologist?.rankingCategory === 'N' || psychologist?.rankingCategory === 'NZ';
  const quarter = useMemo(() => getCurrentQuarter(), []);

  // ─── Panel helpers ────────────────────────────────────────────────────────

  function openPanel(id: PanelId, highlight?: 'universe-5' | 'universe-10') {
    setPanelStack([id]);
    setPanelHighlight(highlight ?? null);
  }
  function navigateInPanel(id: PanelId) { setPanelStack(prev => [...prev, id]); setPanelHighlight(null); }
  function goBackInPanel() { setPanelStack(prev => prev.slice(0, -1)); setPanelHighlight(null); }
  function closePanel() { setPanelStack([]); setPanelHighlight(null); }

  // ─── ViewMode helpers ─────────────────────────────────────────────────────

  function getViewMode(key: string): ViewMode { return viewModes[key] ?? 'process'; }
  function setViewMode(key: string, mode: ViewMode) { setViewModes(prev => ({ ...prev, [key]: mode })); }

  // ─── Data loading ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (isNueva) { setDataLoading(false); return; }
    let cancelled = false;

    async function loadData() {
      setDataLoading(true);
      try {
        const today = new Date();
        const allSessionsResp = await apiCoreAdapter.psychologistSessionsList({
          from: '2020-01-01 00:00:00',
          to: formatDateForApi(today),
          take: 5000,
          status: 'settled,active',
        });
        if (cancelled) return;

        const qStart = quarter.start.getTime();
        const qEnd = Math.min(quarter.end.getTime(), today.getTime());

        const byProcess = new Map<string, {
          patientName: string;
          therapyType: string;
          allTimeSessions: SessionDetail[];
        }>();

        for (const s of allSessionsResp.data) {
          const pid = s.process;
          if (!pid) continue;
          const patientName =
            (Array.isArray(s.patients) && s.patients.length > 0 ? s.patients[0].name : null) ?? 'Paciente';
          const therapyType = s.process_info?.type_name ?? '';
          const entry = byProcess.get(pid) ?? { patientName, therapyType, allTimeSessions: [] };
          entry.allTimeSessions.push({ id: s.id, date: s.date });
          byProcess.set(pid, entry);
        }

        const result: ProcessProgress[] = Array.from(byProcess.entries()).map(([processId, d]) => {
          const sorted = [...d.allTimeSessions].sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
          );
          const totalSessions = sorted.length;

          const inQ = (s: SessionDetail) => {
            const ts = new Date(s.date).getTime();
            return ts >= qStart && ts <= qEnd;
          };

          const after10th = sorted.slice(9);
          const after5th = sorted.slice(4);
          const quarterDetails = sorted.filter(inQ);
          const eligibleDetails = after10th.filter(inQ);
          const eligible5Details = after5th.filter(inQ);

          const eligibleDate10 = sorted[9]?.date ?? null;
          const eligibleDate5 = sorted[4]?.date ?? null;
          const eligible5InQ = after5th.filter(inQ).length;

          return {
            processId,
            patientName: d.patientName,
            therapyType: d.therapyType,
            totalSessions,
            quarterSessions: quarterDetails.length,
            quarterDetails,
            allTimeDetails: sorted,
            eligibleInQ: eligibleDetails.length,
            eligibleDetails,
            eligibleDate10,
            eligible5InQ,
            eligible5Details,
            eligibleDate5,
          };
        });

        setProgressData(
          result
            .filter(p => p.quarterSessions > 0 || p.totalSessions > 0)
            .sort((a, b) => b.eligibleInQ - a.eligibleInQ),
        );
      } catch {
        setProgressData([]);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, [isNueva, quarter]);

  // ─── Derived state ────────────────────────────────────────────────────────

  const eligibleProcesses = progressData.filter(p => p.totalSessions >= 10 && p.eligibleInQ > 0);
  const bronze5Processes = progressData.filter(p => p.totalSessions >= 5 && p.totalSessions < 10 && p.eligible5InQ > 0);
  // threshold session itself doesn't generate eligible sessions — first eligible is threshold+1
  const notEligible5Processes = progressData.filter(p => p.totalSessions < 5 && p.quarterSessions > 0);
  const notEligible10Processes = progressData.filter(p => p.totalSessions < 10 && p.quarterSessions > 0);

  const totalEligibleQ = eligibleProcesses.reduce((sum, p) => sum + p.eligibleInQ, 0);
  // $500 counts ALL 5+ session processes (including 10+, which also have 5+ by definition)
  const allBronzeEligible = progressData.filter(p => p.totalSessions >= 5 && p.eligible5InQ > 0);
  const totalBronzeQ = allBronzeEligible.reduce((sum, p) => sum + p.eligible5InQ, 0);
  const totalQuarterSessions = progressData
    .filter(p => p.quarterSessions > 0)
    .reduce((sum, p) => sum + p.quarterSessions, 0);

  const bronzeAchieved = totalBronzeQ >= 150;
  const silverAchieved = totalEligibleQ >= 150;
  const goldAchieved = totalEligibleQ >= 250;
  const currentTier: TierId | null = goldAchieved ? 'gold' : silverAchieved ? 'silver' : bronzeAchieved ? 'bronze' : null;

  function toggleProcess(key: string) {
    setOpenProcesses(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  // ─── ConceptLink ──────────────────────────────────────────────────────────

  function ConceptLink({ panelId, children, className, highlightSection }: { panelId: Exclude<PanelId, 'hub'>; children: React.ReactNode; className?: string; highlightSection?: 'universe-5' | 'universe-10' }) {
    return (
      <button
        type="button"
        onClick={() => openPanel(panelId, highlightSection)}
        className={`underline decoration-dotted underline-offset-2 font-medium hover:opacity-75 transition-opacity cursor-pointer ${className ?? ''}`}
      >
        {children}
      </button>
    );
  }

  // ─── ViewToggle ───────────────────────────────────────────────────────────

  function ViewToggle({ viewKey }: { viewKey: string }) {
    const mode = getViewMode(viewKey);
    return (
      <div className="flex items-center gap-1 self-start">
        {(['process', 'date'] as ViewMode[]).map(m => (
          <button key={m} type="button"
            onClick={() => setViewMode(viewKey, m)}
            className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${mode === m ? 'bg-violet-100 text-violet-700' : 'text-gray-400 hover:text-gray-600'}`}
          >
            {m === 'process' ? 'Por proceso' : 'Por fecha'}
          </button>
        ))}
      </div>
    );
  }

  // ─── Divider ──────────────────────────────────────────────────────────────

  const Divider = () => (
    <div className="my-2">
      <hr className="border-2 border-[#2D2049] rounded-full" />
    </div>
  );


  // ─── RepasemosConceptos button ────────────────────────────────────────────

  const RepasemosConceptosBtn = () => (
    <button
      type="button"
      onClick={() => openPanel('hub')}
      className="w-full flex items-center justify-between bg-[#2D2049] hover:bg-[#3D2D60] rounded-2xl px-5 py-4 transition-colors text-left"
    >
      <div className="flex items-center gap-3">
        <span className="text-xl">📖</span>
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-white">Repasemos conceptos</span>
          <span className="text-xs text-white/60">Tasa de inactividad, procesos elegibles, bloqueos y más</span>
        </div>
      </div>
      <ChevronRight size={18} className="text-white/70 shrink-0" />
    </button>
  );

  // ─── Asistente Psi+ button ─────────────────────────────────────────────────

  const PsiPlusAssistantCard = () => (
    <button
      type="button"
      onClick={() => setShowAssistant(true)}
      className="w-full flex items-center justify-between bg-[#2D2049] hover:bg-[#3D2D60] rounded-2xl px-5 py-4 transition-colors text-left"
    >
      <div className="flex items-center gap-3">
        <MessageCircle size={18} className="text-white shrink-0" />
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-white">Preguntale al Asistente Psi+</span>
          <span className="text-xs text-white/60">Resolvé dudas sobre el programa, sesiones elegibles, bonos y más</span>
        </div>
      </div>
      <ChevronRight size={18} className="text-white/70 shrink-0" />
    </button>
  );

  // ─── ProcessRow (for 10+ eligible processes, Tab 3) ───────────────────────

  function ProcessRow({ p, rowKey }: { p: ProcessProgress; rowKey: string }) {
    const isRowOpen = openProcesses.has(rowKey);
    return (
      <div key={p.processId} className="flex flex-col gap-1">
        <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
          <div className="flex items-center gap-1.5 min-w-0">
            <button
              type="button"
              onClick={() => toggleProcess(rowKey)}
              className="text-violet-400 hover:text-violet-600 shrink-0 transition-colors"
              aria-label={isRowOpen ? 'Ocultar detalle' : 'Ver detalle'}
            >
              <span className="text-xs">{isRowOpen ? '▲' : '▼'}</span>
            </button>
            <button
              type="button"
              onClick={() => navigate(`/patients?q=${encodeURIComponent(p.patientName)}`)}
              className="text-sm font-medium text-[#2D2049] hover:text-violet-700 hover:underline underline-offset-2 text-left truncate transition-colors"
            >
              {p.patientName}
            </button>
            {p.therapyType && (
              <span className="text-xs text-gray-400 font-normal shrink-0 hidden sm:inline">({p.therapyType})</span>
            )}
          </div>
          <span className="text-sm font-bold text-right tabular-nums text-violet-700">{p.eligibleInQ}</span>
        </div>
        {isRowOpen && (
          <div className="ml-5 flex flex-col gap-1 border-l-2 border-violet-200 pl-3 mt-0.5">
            {p.eligibleDate10 && (
              <p className="text-xs text-gray-400 mb-0.5">
                Umbral de 10 sesiones superado el {formatSessionDate(p.eligibleDate10)}
              </p>
            )}
            {p.eligibleDetails.map((s, idx) => (
              <div key={s.id} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-300 shrink-0" />
                <span className="text-xs text-gray-500">
                  {formatSessionDate(s.date)}
                  <span className="text-gray-400 ml-1">(sesión elegible {idx + 1})</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── ProcessRow5 (for 5+ eligible processes, Tab 2) ───────────────────────

  function ProcessRow5({ p, rowKey }: { p: ProcessProgress; rowKey: string }) {
    const isRowOpen = openProcesses.has(rowKey);
    return (
      <div key={p.processId} className="flex flex-col gap-1">
        <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
          <div className="flex items-center gap-1.5 min-w-0">
            <button
              type="button"
              onClick={() => toggleProcess(rowKey)}
              className="text-amber-400 hover:text-amber-600 shrink-0 transition-colors"
              aria-label={isRowOpen ? 'Ocultar detalle' : 'Ver detalle'}
            >
              <span className="text-xs">{isRowOpen ? '▲' : '▼'}</span>
            </button>
            <button
              type="button"
              onClick={() => navigate(`/patients?q=${encodeURIComponent(p.patientName)}`)}
              className="text-sm font-medium text-[#2D2049] hover:text-violet-700 hover:underline underline-offset-2 text-left truncate transition-colors"
            >
              {p.patientName}
            </button>
            {p.therapyType && (
              <span className="text-xs text-gray-400 font-normal shrink-0 hidden sm:inline">({p.therapyType})</span>
            )}
          </div>
          <span className="text-sm font-bold text-right tabular-nums text-amber-700">{p.eligible5InQ}</span>
        </div>
        {isRowOpen && (
          <div className="ml-5 flex flex-col gap-1 border-l-2 border-amber-200 pl-3 mt-0.5">
            {p.eligibleDate5 && (
              <p className="text-xs text-gray-400 mb-0.5">
                Umbral de 5 sesiones superado el {formatSessionDate(p.eligibleDate5)}
              </p>
            )}
            {p.eligible5Details.map((s, idx) => (
              <div key={s.id} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300 shrink-0" />
                <span className="text-xs text-gray-500">
                  {formatSessionDate(s.date)}
                  <span className="text-gray-400 ml-1">(sesión elegible {idx + 1})</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── Guard: nueva ─────────────────────────────────────────────────────────

  if (isNueva) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <div className="bg-violet-100 rounded-2xl p-4"><span className="text-3xl">🌱</span></div>
        <p className="text-sm font-semibold text-[#2D2049]">Estás completando tu período inicial</p>
        <p className="text-sm text-gray-500 max-w-sm">
          El Programa Psi+ 2.0 estará disponible para vos en cuanto completes esta etapa de adaptación.
        </p>
      </div>
    );
  }

  // ─── Header colors (for Este trimestre cobrarías) ─────────────────────────

  const headerBorderColor = currentTier ? 'border-emerald-400' : 'border-gray-200';
  const headerBgColor = currentTier ? 'bg-emerald-50' : 'bg-gray-50';
  const headerLabelColor = currentTier ? 'text-emerald-600' : 'text-gray-500';
  const headerValueColor = currentTier ? 'text-emerald-700' : 'text-gray-400';
  const headerQuarterColor = 'text-emerald-600';

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6">

      <ConceptPanel
        panelStack={panelStack}
        highlightSection={panelHighlight}
        onClose={closePanel}
        onNavigate={navigateInPanel}
        onBack={goBackInPanel}
        onNavigateToInactividad={onNavigateToInactividad}
      />

      {/* ── Sub-tab bar ──────────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {[
          { id: 'detalles', label: 'Detalles del programa' },
          { id: 'bono-500', label: 'Bono $500' },
          { id: 'bonos-1000-2000', label: 'Bonos $1.000 y $2.000' },
        ].map(tab => (
          <button key={tab.id} type="button"
            onClick={() => setSubTab(tab.id as SubTab)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              subTab === tab.id ? 'bg-white text-[#2D2049] shadow-sm font-semibold' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          SUB-TAB 1: Detalles del programa
      ════════════════════════════════════════════════════════════════════ */}
      {subTab === 'detalles' && (
        <>
          {/* ── 1. Intro ────────────────────────────────────────────────── */}
          <div className="bg-[#F3EFFF] rounded-2xl px-6 py-6 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-bold uppercase tracking-widest text-[#4A3D73]">Beneficios</p>
              <p className="text-2xl font-bold text-[#2D2049]">Programa Psi+ 2.0</p>
            </div>

            <p className="text-sm text-[#4A3D73] leading-relaxed">
              Hoy el foco más grande está puesto en la <strong>adherencia</strong> de los{' '}
              <ConceptLink panelId="procesos-terapeuticos" className="text-[#4A3D73]">procesos terapéuticos</ConceptLink>.
              Por esto mismo, este programa de bonificaciones apunta a <strong>premiar a quienes mantienen a sus pacientes comprometidos</strong> con sus procesos.
            </p>

            <p className="text-sm text-[#4A3D73] leading-relaxed">
              Estas bonificaciones te permiten acceder a <strong>bonos de hasta USD 2.000</strong> de manera trimestral, con una franja inicial de <strong>USD 500</strong> — basados únicamente en la <strong>cantidad de sesiones realizadas</strong> por trimestre, de{' '}
              <ConceptLink panelId="procesos-elegibles" className="text-[#4A3D73]">procesos terapéuticos elegibles</ConceptLink>.
              Esto quiere decir que <strong>cualquier psicólogo puede acceder</strong> a estas bonificaciones.
            </p>

            <p className="text-sm text-[#4A3D73] leading-relaxed">
              El <strong>esquema trimestral</strong> apunta a mejorar la <strong>previsibilidad, proyección y claridad</strong> de la información — para que sepas qué falta concretamente para acceder al bono más alto posible, gestionar tus sesiones con tiempo y aumentar tu remuneración percibida. Se accede únicamente al <strong>bono más alto que se alcance</strong>.
            </p>

            <div className="flex flex-col gap-2 pt-1">
              <p className="text-xs font-bold uppercase tracking-widest text-[#4A3D73]">Bonos disponibles</p>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3 p-3 bg-white/60 rounded-xl border border-amber-200">
                  <span className="text-xl shrink-0">🥉</span>
                  <div>
                    <p className="text-sm font-bold text-amber-700">USD 500</p>
                    <p className="text-xs text-[#4A3D73]">
                      procesos de 5+ sesiones · 150{' '}
                      <ConceptLink panelId="procesos-elegibles" className="text-[#4A3D73]" highlightSection="universe-5">sesiones elegibles</ConceptLink>
                      {' '}en el trimestre
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/60 rounded-xl border border-sky-200">
                  <span className="text-xl shrink-0">🥈</span>
                  <div>
                    <p className="text-sm font-bold text-sky-700">USD 1.000</p>
                    <p className="text-xs text-[#4A3D73]">
                      procesos de 10+ sesiones · 150{' '}
                      <ConceptLink panelId="procesos-elegibles" className="text-[#4A3D73]" highlightSection="universe-10">sesiones elegibles</ConceptLink>
                      {' '}en el trimestre
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/60 rounded-xl border border-blue-200">
                  <span className="text-xl shrink-0">🥇</span>
                  <div>
                    <p className="text-sm font-bold text-blue-700">USD 2.000</p>
                    <p className="text-xs text-[#4A3D73]">
                      procesos de 10+ sesiones · 250{' '}
                      <ConceptLink panelId="procesos-elegibles" className="text-[#4A3D73]" highlightSection="universe-10">sesiones elegibles</ConceptLink>
                      {' '}en el trimestre
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-400 leading-relaxed pt-1">
                Los bonos son excluyentes — se cobra solo el más alto que se alcance. Las sesiones elegibles no se acumulan entre trimestres.
              </p>
            </div>
          </div>

          <Divider />

          {/* ── 2. Hilo conductor ───────────────────────────────────────── */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-5 flex flex-col gap-3">
            <div className="flex flex-col gap-0.5">
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">El hilo conductor</p>
              <p className="text-sm font-semibold text-[#2D2049]">¿Cómo hago para acceder a un bono mayor?</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { label: 'Tasa de inactividad baja', panelId: 'tasa-inactividad' as const },
                null,
                { label: 'Más asignaciones de pacientes', panelId: null },
                null,
                { label: 'Más pacientes, más sesiones', panelId: null },
                null,
                { label: 'Más sesiones, más bono', panelId: null },
              ].map((item, i) => item === null ? (
                <span key={i} className="text-emerald-700 font-bold text-sm">→</span>
              ) : item.panelId ? (
                <button
                  key={i}
                  type="button"
                  onClick={() => openPanel(item.panelId!)}
                  className="bg-emerald-200 text-emerald-800 text-xs font-semibold px-3 py-1.5 rounded-full underline decoration-dotted underline-offset-2 hover:bg-emerald-300 transition-colors"
                >
                  {item.label}
                </button>
              ) : (
                <span key={i} className="bg-emerald-200 text-emerald-800 text-xs font-semibold px-3 py-1.5 rounded-full">
                  {item.label}
                </span>
              ))}
            </div>
          </div>

          {/* ── 3. ¿Cómo maximizo? ──────────────────────────────────────── */}
          <div className="border-2 border-black bg-violet-100 rounded-2xl overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenMaximizar(b => !b)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-violet-200/60 transition-colors"
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-[#2D2049]">¿Cómo maximizo mis chances de acceder al bono más alto?</span>
                <span className="text-xs text-gray-500">Factores que podés gestionar vos para mejorar tu acceso al bono</span>
              </div>
              <span className="text-xs text-gray-500 ml-3 shrink-0">{openMaximizar ? '▲' : '▼'}</span>
            </button>

            {openMaximizar && (
              <div className="px-5 pb-5 flex flex-col gap-5 border-t border-black/20 pt-5">
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong>Nosotros solo contamos las sesiones elegibles</strong> para determinar tu bono trimestral. Lo que podés controlar — y que influye indirectamente — son estas variables. Te damos la información para que puedas tomar las mejores decisiones.
                </p>

                {[
                  {
                    emoji: '🎯',
                    title: 'Tasa de inactividad',
                    panelId: 'tasa-inactividad' as const,
                    text: 'La variable más importante. Cuando tus pacientes se mantienen activos y agendando regularmente, el sistema te habilita para recibir nuevas asignaciones. Una tasa de inactividad menor al 10%, sostenida en el tiempo, deriva en posibilidad de más asignaciones → más procesos → más sesiones elegibles para el bono.',
                  },
                  {
                    emoji: '🔓',
                    title: 'Límite de pacientes pedido por vos',
                    panelId: null,
                    text: null,
                    jsx: (
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Si en algún momento nos pediste limitar la cantidad de pacientes que recibís, ese tope puede estar frenando nuevas asignaciones, incluso si el sistema te consideraría apto. La buena noticia: podés revisarlo y{' '}
                        <button
                          type="button"
                          onClick={() => navigate('/profile?tab=attention')}
                          className="underline decoration-dotted underline-offset-2 font-medium text-violet-700 hover:text-violet-900 transition-colors"
                        >
                          ajustarlo desde tu perfil, en &ldquo;Sobre tu atención&rdquo;
                        </button>.
                      </p>
                    ),
                  },
                  {
                    emoji: '📅',
                    title: 'Bloqueos de agenda',
                    panelId: 'bloqueos' as const,
                    text: 'Los días off y las vacaciones son completamente válidos — pero mientras dura un bloqueo, no recibís nuevas asignaciones. Si sabés que vas a tener más disponibilidad próximamente, abrir la agenda a tiempo te da más margen para sumar procesos antes de que cierre el trimestre.',
                  },
                  {
                    emoji: '🗓️',
                    title: 'Horarios disponibles',
                    panelId: null,
                    text: 'Si tus pacientes actuales no encuentran horarios para agendar sus sesiones, los procesos pierden continuidad. Mantener una oferta de horarios variada facilita que los procesos lleguen y superen el umbral de sesiones — y que las siguientes empiecen a computar como elegibles.',
                  },
                ].map((point, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <span className="text-xl shrink-0 mt-0.5">{point.emoji}</span>
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-semibold text-[#2D2049]">
                        {point.panelId ? (
                          <ConceptLink panelId={point.panelId} className="text-[#2D2049]">{point.title}</ConceptLink>
                        ) : point.title}
                      </p>
                      {'jsx' in point && point.jsx ? point.jsx : (
                        <p className="text-sm text-gray-600 leading-relaxed">{point.text}</p>
                      )}
                    </div>
                  </div>
                ))}

                <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                  <p className="text-sm text-[#4A3D73] leading-relaxed">
                    <strong>Una aclaración importante:</strong> nosotros no vamos a limitar tu acceso a los bonos por ninguno de estos factores. El bono depende únicamente de tus sesiones elegibles. Las herramientas para maximizar tus chances están en tus manos — y esta pantalla existe para que tengas toda la información necesaria.
                  </p>
                </div>
              </div>
            )}
          </div>

          <Divider />

          {/* ── 4. Este trimestre cobrarías (header only) ───────────────── */}
          <div className={`rounded-2xl border-2 overflow-hidden ${currentTier ? headerBorderColor : 'border-gray-200'}`}>
            <div className={`px-6 py-5 flex items-center gap-4 ${currentTier ? headerBgColor : 'bg-gray-50'}`}>
              {dataLoading ? (
                <div className="flex items-center gap-3 py-1">
                  <CircularProgress />
                  <p className="text-sm text-gray-400">Calculando tu bono...</p>
                </div>
              ) : (
                <>
                  <span className={`shrink-0 ${currentTier ? 'text-4xl' : 'text-2xl'}`}>
                    {currentTier ? TIER_CONFIG[currentTier].emoji : '⏳'}
                  </span>
                  <div className="flex flex-col gap-0.5">
                    <p className={`text-xs font-bold uppercase tracking-widest ${currentTier ? headerLabelColor : 'text-gray-500'}`}>
                      Este trimestre cobrarías
                    </p>
                    <p className={`font-extrabold ${currentTier ? `text-3xl ${headerValueColor}` : 'text-xl text-gray-400'}`}>
                      {currentTier ? TIER_CONFIG[currentTier].bonus : 'Ningún bono todavía'}
                    </p>
                    {currentTier && <p className={`text-xs ${headerQuarterColor}`}>{quarter.label}</p>}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Disclaimer ──────────────────────────────────────────────── */}
          <p className="text-xs text-center text-gray-400 font-medium">
            Recordá que sólo se accede al bono más alto ganado en el trimestre.
          </p>

          {/* ── 5. 3 simplified bonus cards ─────────────────────────────── */}
          {dataLoading ? (
            <div className="flex justify-center py-4"><CircularProgress /></div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Bronze $500 card */}
              {(() => {
                const superseded = bronzeAchieved && silverAchieved;
                const pct500 = Math.min(100, Math.round(totalBronzeQ / 150 * 100));
                if (superseded) {
                  return (
                    <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl px-5 py-4 flex flex-col gap-3 opacity-70">
                      <div className="flex flex-col gap-0.5">
                        <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest">Bono trimestral</p>
                        <p className="text-lg font-bold text-gray-400">USD $500</p>
                      </div>
                      <p className="text-xs text-gray-400">
                        Universo:{' '}
                        <ConceptLink panelId="procesos-elegibles" className="text-gray-400" highlightSection="universe-5">procesos de 5+ sesiones</ConceptLink>
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 rounded-full h-2 bg-gray-200 overflow-hidden">
                          <div className="h-2 rounded-full bg-gray-400 transition-all" style={{ width: `${pct500}%` }} />
                        </div>
                        <span className="text-xs font-bold text-gray-400 tabular-nums">{totalBronzeQ} / 150</span>
                      </div>
                      <p className="text-sm font-semibold text-gray-500">🔥 Este bono no aplica, ya que alcanzaste uno mejor para este trimestre!</p>
                      <div className="flex justify-end mt-1">
                        <button type="button" onClick={() => setSubTab('bono-500')}
                          className="text-xs font-semibold text-gray-400 hover:text-gray-600 underline decoration-dotted underline-offset-2 transition-colors"
                        >
                          Ver detalles →
                        </button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="bg-violet-50 border-2 border-violet-200 rounded-2xl px-5 py-4 flex flex-col gap-3">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-xs text-violet-500 font-semibold uppercase tracking-widest">Bono trimestral</p>
                      <p className="text-lg font-bold text-[#2D2049]">USD $500</p>
                    </div>
                    <p className="text-xs text-violet-600">
                      Universo:{' '}
                      <ConceptLink panelId="procesos-elegibles" className="text-violet-600" highlightSection="universe-5">procesos de 5+ sesiones</ConceptLink>
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 rounded-full h-2 bg-violet-200 overflow-hidden">
                        <div className="h-2 rounded-full bg-violet-500 transition-all" style={{ width: `${pct500}%` }} />
                      </div>
                      <span className="text-xs font-bold text-violet-700 tabular-nums">{totalBronzeQ} / 150</span>
                    </div>
                    {bronzeAchieved && (
                      <p className="text-sm font-semibold text-violet-800">🎉 ¡Felicitaciones! Desbloqueaste este bono.</p>
                    )}
                    <div className="flex justify-end mt-1">
                      <button type="button" onClick={() => setSubTab('bono-500')}
                        className="text-xs font-semibold text-violet-600 hover:text-violet-800 underline decoration-dotted underline-offset-2 transition-colors"
                      >
                        Ver detalles →
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Silver $1.000 card */}
              {(() => {
                const superseded = silverAchieved && goldAchieved;
                const pct1000 = Math.min(100, Math.round(totalEligibleQ / 150 * 100));
                if (superseded) {
                  return (
                    <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl px-5 py-4 flex flex-col gap-3 opacity-70">
                      <div className="flex flex-col gap-0.5">
                        <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest">Bono trimestral</p>
                        <p className="text-lg font-bold text-gray-400">USD $1.000</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 rounded-full h-2 bg-gray-200 overflow-hidden">
                          <div className="h-2 rounded-full bg-gray-400 transition-all" style={{ width: `${pct1000}%` }} />
                        </div>
                        <span className="text-xs font-bold text-gray-400 tabular-nums">{totalEligibleQ} / 150</span>
                      </div>
                      <p className="text-sm font-semibold text-gray-500">🔥 Este bono no aplica, ya que alcanzaste uno mejor para este trimestre!</p>
                      <div className="flex justify-end mt-1">
                        <button type="button" onClick={() => setSubTab('bonos-1000-2000')}
                          className="text-xs font-semibold text-gray-400 hover:text-gray-600 underline decoration-dotted underline-offset-2 transition-colors"
                        >
                          Ver detalles →
                        </button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="bg-violet-100 border-2 border-violet-300 rounded-2xl px-5 py-4 flex flex-col gap-3">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-xs text-violet-500 font-semibold uppercase tracking-widest">Bono trimestral</p>
                      <p className="text-lg font-bold text-[#2D2049]">USD $1.000</p>
                    </div>
                    <p className="text-xs text-violet-600">
                      Universo:{' '}
                      <ConceptLink panelId="procesos-elegibles" className="text-violet-600" highlightSection="universe-10">procesos de 10+ sesiones</ConceptLink>
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 rounded-full h-2 bg-violet-200 overflow-hidden">
                        <div className="h-2 rounded-full bg-violet-500 transition-all" style={{ width: `${pct1000}%` }} />
                      </div>
                      <span className="text-xs font-bold text-violet-700 tabular-nums">{totalEligibleQ} / 150</span>
                    </div>
                    {silverAchieved && (
                      <p className="text-sm font-semibold text-violet-800">🎉 ¡Felicitaciones! Desbloqueaste este bono.</p>
                    )}
                    <div className="flex justify-end mt-1">
                      <button type="button" onClick={() => setSubTab('bonos-1000-2000')}
                        className="text-xs font-semibold text-violet-600 hover:text-violet-800 underline decoration-dotted underline-offset-2 transition-colors"
                      >
                        Ver detalles →
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Gold $2.000 card */}
              {(() => {
                const pct2000 = Math.min(100, Math.round(totalEligibleQ / 250 * 100));
                return (
                  <div className="bg-violet-200 border-2 border-violet-400 rounded-2xl px-5 py-4 flex flex-col gap-3">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-xs text-violet-600 font-semibold uppercase tracking-widest">Bono trimestral</p>
                      <p className="text-lg font-bold text-[#2D2049]">USD $2.000</p>
                    </div>
                    <p className="text-xs text-violet-700">
                      Universo:{' '}
                      <ConceptLink panelId="procesos-elegibles" className="text-violet-700" highlightSection="universe-10">procesos de 10+ sesiones</ConceptLink>
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 rounded-full h-2 bg-violet-300 overflow-hidden">
                        <div className="h-2 rounded-full bg-violet-600 transition-all" style={{ width: `${pct2000}%` }} />
                      </div>
                      <span className="text-xs font-bold text-violet-800 tabular-nums">{totalEligibleQ} / 250</span>
                    </div>
                    {goldAchieved && (
                      <p className="text-sm font-semibold text-violet-900">🎉 ¡Felicitaciones! Desbloqueaste este bono.</p>
                    )}
                    {silverAchieved && !goldAchieved && (
                      <p className="text-sm font-semibold text-violet-800">🎯 ¡Ya casi desbloqueás este bono! Te faltan {250 - totalEligibleQ} sesiones elegibles para acceder.</p>
                    )}
                    <div className="flex justify-end mt-1">
                      <button type="button" onClick={() => setSubTab('bonos-1000-2000')}
                        className="text-xs font-semibold text-violet-700 hover:text-violet-900 underline decoration-dotted underline-offset-2 transition-colors"
                      >
                        Ver detalles →
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          <Divider />

          {/* ── 6. MetricCards ──────────────────────────────────────────── */}
          {!dataLoading && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 bg-violet-100 border border-violet-200 rounded-xl px-4 py-3">
                <p className="text-sm font-bold text-violet-800 uppercase tracking-wide flex-1">
                  {quarter.detailLabel}
                </p>
                <InfoTooltip
                  text="Cada bono tiene su propio universo: el bono de $500 cuenta sesiones a partir de la 5ª de procesos con 5+ sesiones acumuladas. Los bonos de $1.000 y $2.000 cuentan sesiones a partir de la 10ª de procesos con 10+ sesiones acumuladas. Los procesos de 10+ sesiones contribuyen a los tres bonos."
                  placement="top"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <MetricCard
                  icon={<span className="text-base">📅</span>}
                  value={String(totalQuarterSessions)}
                  label="Sesiones realizadas en el trimestre"
                  tooltipText="Total de sesiones realizadas este trimestre, sin importar si son elegibles para algún bono o no."
                />
                <MetricCard
                  icon={<span className="text-base">⭐</span>}
                  value={String(totalEligibleQ)}
                  label="Elegibles para bonos de $1.000 y $2.000"
                  tooltipText="Sesiones a partir de la 10ª de procesos con 10 o más sesiones acumuladas, realizadas en este trimestre. Cuentan para el bono de $1.000 (meta: 150 sesiones) y el de $2.000 (meta: 250 sesiones)."
                />
                <MetricCard
                  icon={<span className="text-base">🥉</span>}
                  value={String(totalBronzeQ)}
                  label="Elegibles para bono de $500"
                  tooltipText="Sesiones a partir de la 5ª de procesos con 5 o más sesiones acumuladas, realizadas en este trimestre. Meta: 150 sesiones."
                />
              </div>
            </div>
          )}

          <Divider />

          {/* ── 7. Repasemos conceptos ──────────────────────────────────── */}
          <RepasemosConceptosBtn />
          <PsiPlusAssistantCard />

          <Divider />
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          SUB-TAB 2: Bono $500
      ════════════════════════════════════════════════════════════════════ */}
      {subTab === 'bono-500' && (
        <>
          {/* Header card de estado para bronze */}
          {(() => {
            const superseded = bronzeAchieved && silverAchieved;
            const borderCls = superseded ? 'border-gray-200' : bronzeAchieved ? 'border-amber-400' : 'border-gray-200';
            const bgCls = superseded ? 'bg-gray-50' : bronzeAchieved ? 'bg-amber-50' : 'bg-gray-50';
            return (
              <div className={`rounded-2xl border-2 overflow-hidden ${borderCls}`}>
                <div className={`px-6 py-5 flex items-center gap-4 ${bgCls}`}>
                  {dataLoading ? (
                    <div className="flex items-center gap-3 py-1">
                      <CircularProgress />
                      <p className="text-sm text-gray-400">Calculando tu bono...</p>
                    </div>
                  ) : (
                    <>
                      <span className={`shrink-0 ${bronzeAchieved ? 'text-4xl' : 'text-2xl'}`}>
                        {bronzeAchieved ? '🥉' : '⏳'}
                      </span>
                      <div className="flex flex-col gap-1">
                        <p className={`text-xs font-bold uppercase tracking-widest ${superseded ? 'text-gray-400' : bronzeAchieved ? 'text-amber-600' : 'text-gray-500'}`}>
                          Bono de $500
                        </p>
                        {superseded && (
                          <p className="text-sm font-semibold text-gray-500">🔥 Este bono no aplica, ya que alcanzaste uno mejor para este trimestre!</p>
                        )}
                        {bronzeAchieved && !superseded && (
                          <p className="text-sm font-semibold text-amber-700">🎉 ¡Felicitaciones! Desbloqueaste este bono.</p>
                        )}
                        {!bronzeAchieved && (
                          <p className="text-xl font-extrabold text-gray-400">No alcanzado todavía</p>
                        )}
                        {bronzeAchieved && !superseded && <p className="text-xs text-amber-600">{quarter.label}</p>}
                        {!bronzeAchieved && (
                          <p className="text-xs text-gray-400">
                            {totalBronzeQ} / 150{' '}
                            <ConceptLink panelId="procesos-elegibles" className="text-gray-400" highlightSection="universe-5">sesiones elegibles</ConceptLink>
                            {' '}este trimestre (universo 5+)
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}


          {/* Procesos elegibles para $500 (todos los de 5+ sesiones) */}
          {dataLoading ? (
            <div className="flex justify-center py-4"><CircularProgress /></div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-amber-800">
                  {allBronzeEligible.length} proceso{allBronzeEligible.length !== 1 ? 's' : ''} elegibles · {totalBronzeQ} sesiones elegibles
                </p>
                <ViewToggle viewKey="bono-500-eligible" />
              </div>

              {allBronzeEligible.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  No hay procesos con 5+ sesiones acumuladas y sesiones elegibles este trimestre.
                </p>
              ) : getViewMode('bono-500-eligible') === 'process' ? (
                <>
                  <div className="grid grid-cols-[1fr_auto] gap-2 text-xs font-bold text-gray-400 uppercase tracking-wide pb-1 border-b border-gray-100">
                    <span>Proceso</span>
                    <span className="text-right">Elegibles en el trimestre</span>
                  </div>
                  {allBronzeEligible.map(p => (
                    <ProcessRow5 key={p.processId} p={p} rowKey={`b500-${p.processId}`} />
                  ))}
                  <div className="grid grid-cols-[1fr_auto] gap-2 pt-2 border-t border-gray-200">
                    <span className="text-xs font-bold text-gray-600">Total sesiones elegibles en el trimestre</span>
                    <span className="text-sm font-bold text-amber-700 text-right tabular-nums">{totalBronzeQ}</span>
                  </div>
                </>
              ) : (
                <>
                  {(() => {
                    const flat = allBronzeEligible
                      .flatMap(p => p.eligible5Details.map((s, idx) => ({ ...s, patientName: p.patientName, processId: p.processId, eligibleIdx: idx + 1 })))
                      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                    return flat.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-2">No hay sesiones elegibles este trimestre.</p>
                    ) : flat.map(s => (
                      <div key={s.id} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-300 shrink-0" />
                        <span className="text-xs text-gray-500">
                          {formatSessionDate(s.date)} — <span className="font-medium">{s.patientName}</span>
                          <span className="text-gray-400 ml-1">(sesión elegible #{s.eligibleIdx})</span>
                        </span>
                      </div>
                    ));
                  })()}
                </>
              )}
            </div>
          )}

          <Divider />

          {/* Collapsible: procesos sin umbral para $500 */}
          {!dataLoading && notEligible5Processes.length > 0 && (
            <div className="border border-gray-200 rounded-2xl overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenNotEligible5Tab2(b => !b)}
                className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-gray-600">Procesos sin umbral para el bono de $500</span>
                  <span className="text-xs text-gray-400">
                    {notEligible5Processes.length} proceso{notEligible5Processes.length !== 1 ? 's' : ''} · sin sesiones elegibles aún — desde la 5ª sesión empieza a contar
                  </span>
                </div>
                <span className="text-xs text-gray-400 ml-2 shrink-0">{openNotEligible5Tab2 ? '▲' : '▼'}</span>
              </button>
              {openNotEligible5Tab2 && (
                <div className="px-5 py-3 bg-white flex flex-col gap-3">
                  <ViewToggle viewKey="not-eligible-5-tab2" />
                  {getViewMode('not-eligible-5-tab2') === 'process' ? (
                    notEligible5Processes.map(p => {
                      const remaining = 5 - p.totalSessions;
                      const isExpanded = expandedNE5.has(p.processId);
                      const isOutsideExpanded = expandedNE5Outside.has(p.processId);
                      const outsideSessions = p.allTimeDetails.filter(
                        s => !p.quarterDetails.find(q => q.id === s.id),
                      );
                      return (
                        <div key={p.processId} className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="px-4 py-3 flex items-start justify-between gap-4">
                            <button
                              type="button"
                              onClick={() => navigate(`/patients?q=${encodeURIComponent(p.patientName)}`)}
                              className="text-sm font-medium text-[#2D2049] hover:text-violet-700 hover:underline underline-offset-2 text-left transition-colors truncate min-w-0"
                            >
                              {p.patientName}
                            </button>
                            <div className="flex flex-col items-end shrink-0 gap-0.5">
                              <button
                                type="button"
                                onClick={() => setExpandedNE5(prev => {
                                  const next = new Set(prev);
                                  isExpanded ? next.delete(p.processId) : next.add(p.processId);
                                  return next;
                                })}
                                className="text-xs text-gray-600 font-medium flex items-center gap-1 hover:text-violet-700 transition-colors"
                              >
                                {p.totalSessions} sesión{p.totalSessions !== 1 ? 'es' : ''} acumuladas {isExpanded ? '▲' : '▼'}
                              </button>
                              <span className="text-xs text-gray-400">
                                {remaining <= 0
                                  ? 'Umbral alcanzado — la próxima ya clasifica para el bono de $500'
                                  : `Necesita ${remaining} sesión${remaining !== 1 ? 'es' : ''} más para cruzar el umbral — desde la 5ª serán elegibles`}
                              </span>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="px-4 pb-3 pt-1 flex flex-col gap-2 border-t border-gray-100">
                              {/* Sesiones este trimestre — siempre abierto */}
                              <div className="flex flex-col gap-1">
                                <p className="text-xs font-semibold text-gray-500">Sesiones este trimestre</p>
                                {p.quarterDetails.length === 0 ? (
                                  <p className="text-xs text-gray-400 pl-1">Sin sesiones este trimestre.</p>
                                ) : (
                                  p.quarterDetails
                                    .slice()
                                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                    .map(s => {
                                      const globalIdx = p.allTimeDetails.findIndex(x => x.id === s.id) + 1;
                                      return (
                                        <div key={s.id} className="flex items-center gap-2">
                                          <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                                          <span className="text-xs text-gray-500">
                                            {formatSessionDate(s.date)}
                                            <span className="text-gray-400 ml-1">· sesión #{globalIdx} acumulada</span>
                                          </span>
                                        </div>
                                      );
                                    })
                                )}
                              </div>
                              {/* Sesiones fuera del trimestre — colapsable, cerrado por default */}
                              {outsideSessions.length > 0 && (
                                <div className="flex flex-col gap-1">
                                  <button
                                    type="button"
                                    onClick={() => setExpandedNE5Outside(prev => {
                                      const next = new Set(prev);
                                      isOutsideExpanded ? next.delete(p.processId) : next.add(p.processId);
                                      return next;
                                    })}
                                    className="text-xs text-gray-400 flex items-center gap-1 hover:text-gray-600 transition-colors"
                                  >
                                    Sesiones fuera del trimestre ({outsideSessions.length}) {isOutsideExpanded ? '▲' : '▼'}
                                  </button>
                                  {isOutsideExpanded && outsideSessions
                                    .slice()
                                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                    .map(s => {
                                      const globalIdx = p.allTimeDetails.findIndex(x => x.id === s.id) + 1;
                                      return (
                                        <div key={s.id} className="flex items-center gap-2">
                                          <span className="w-1.5 h-1.5 rounded-full bg-gray-200 shrink-0" />
                                          <span className="text-xs text-gray-400">
                                            {formatSessionDate(s.date)}
                                            <span className="ml-1">· sesión #{globalIdx} acumulada</span>
                                          </span>
                                        </div>
                                      );
                                    })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex flex-col gap-1">
                      {notEligible5Processes
                        .flatMap(p => p.quarterDetails.map(s => ({ ...s, patientName: p.patientName })))
                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                        .map(s => (
                          <div key={s.id} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                            <span className="text-xs text-gray-500">
                              {formatSessionDate(s.date)} — <span className="font-medium">{s.patientName}</span>
                              <span className="text-gray-400 ml-1">(no elegible)</span>
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <Divider />
          <RepasemosConceptosBtn />
          <PsiPlusAssistantCard />
          <Divider />
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          SUB-TAB 3: Bonos $1.000 y $2.000
      ════════════════════════════════════════════════════════════════════ */}
      {subTab === 'bonos-1000-2000' && (
        <>
          {/* Silver header card */}
          {(() => {
            const superseded = silverAchieved && goldAchieved;
            const borderCls = superseded ? 'border-gray-200' : silverAchieved ? 'border-sky-400' : 'border-gray-200';
            const bgCls = superseded ? 'bg-gray-50' : silverAchieved ? 'bg-sky-50' : 'bg-gray-50';
            return (
              <div className={`rounded-2xl border-2 overflow-hidden ${borderCls}`}>
                <div className={`px-6 py-5 flex items-center gap-4 ${bgCls}`}>
                  {dataLoading ? (
                    <div className="flex items-center gap-3 py-1">
                      <CircularProgress />
                      <p className="text-sm text-gray-400">Calculando...</p>
                    </div>
                  ) : (
                    <>
                      <span className={`shrink-0 ${silverAchieved ? 'text-4xl' : 'text-2xl'}`}>
                        {silverAchieved ? '🥈' : '⏳'}
                      </span>
                      <div className="flex flex-col gap-1">
                        <p className={`text-xs font-bold uppercase tracking-widest ${superseded ? 'text-gray-400' : silverAchieved ? 'text-sky-600' : 'text-gray-500'}`}>
                          Bono de $1.000
                        </p>
                        {superseded && (
                          <p className="text-sm font-semibold text-gray-500">🔥 Este bono no aplica, ya que alcanzaste uno mejor para este trimestre!</p>
                        )}
                        {silverAchieved && !superseded && (
                          <p className="text-sm font-semibold text-sky-700">🎉 ¡Felicitaciones! Desbloqueaste este bono.</p>
                        )}
                        {!silverAchieved && (
                          <p className="text-xl font-extrabold text-gray-400">No alcanzado todavía</p>
                        )}
                        {silverAchieved && !superseded && <p className="text-xs text-sky-600">{quarter.label}</p>}
                        {!silverAchieved && (
                          <p className="text-xs text-gray-400">
                            {totalEligibleQ} / 150{' '}
                            <ConceptLink panelId="procesos-elegibles" className="text-gray-400" highlightSection="universe-10">sesiones elegibles</ConceptLink>
                            {' '}este trimestre (universo 10+)
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Gold header card */}
          <div className={`rounded-2xl border-2 overflow-hidden ${goldAchieved ? 'border-emerald-400' : 'border-gray-200'}`}>
            <div className={`px-6 py-5 flex items-center gap-4 ${goldAchieved ? 'bg-emerald-50' : 'bg-gray-50'}`}>
              {dataLoading ? (
                <div className="flex items-center gap-3 py-1">
                  <CircularProgress />
                  <p className="text-sm text-gray-400">Calculando...</p>
                </div>
              ) : (
                <>
                  <span className={`shrink-0 ${goldAchieved ? 'text-4xl' : 'text-2xl'}`}>
                    {goldAchieved ? '🥇' : '⏳'}
                  </span>
                  <div className="flex flex-col gap-1">
                    <p className={`text-xs font-bold uppercase tracking-widest ${goldAchieved ? 'text-emerald-600' : 'text-gray-500'}`}>
                      Bono de $2.000
                    </p>
                    {goldAchieved && (
                      <p className="text-sm font-semibold text-emerald-700">🎉 ¡Felicitaciones! Desbloqueaste este bono.</p>
                    )}
                    {silverAchieved && !goldAchieved && (
                      <p className="text-sm font-semibold text-violet-700">🎯 ¡Ya casi desbloqueás este bono! Te faltan {250 - totalEligibleQ} sesiones elegibles para acceder.</p>
                    )}
                    {!silverAchieved && !goldAchieved && (
                      <p className="text-xl font-extrabold text-gray-400">No alcanzado todavía</p>
                    )}
                    {goldAchieved && <p className="text-xs text-emerald-600">{quarter.label}</p>}
                    {!goldAchieved && (
                      <p className="text-xs text-gray-400">
                        {totalEligibleQ} / 250{' '}
                        <ConceptLink panelId="procesos-elegibles" className="text-gray-400" highlightSection="universe-10">sesiones elegibles</ConceptLink>
                        {' '}este trimestre (universo 10+)
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>


          {/* Procesos elegibles 10+ */}
          {dataLoading ? (
            <div className="flex justify-center py-4"><CircularProgress /></div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-violet-800">
                  {eligibleProcesses.length} proceso{eligibleProcesses.length !== 1 ? 's' : ''} elegibles · {totalEligibleQ} sesiones elegibles
                </p>
                <ViewToggle viewKey="bonos-1000-eligible" />
              </div>

              {eligibleProcesses.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  No hay procesos con 10+ sesiones acumuladas todavía.
                </p>
              ) : getViewMode('bonos-1000-eligible') === 'process' ? (
                <>
                  <div className="grid grid-cols-[1fr_auto] gap-2 text-xs font-bold text-gray-400 uppercase tracking-wide pb-1 border-b border-gray-100">
                    <span>Proceso</span>
                    <span className="text-right">Elegibles en el trimestre</span>
                  </div>
                  {eligibleProcesses.map(p => (
                    <ProcessRow key={p.processId} p={p} rowKey={`b1000-${p.processId}`} />
                  ))}
                  <div className="grid grid-cols-[1fr_auto] gap-2 pt-2 border-t border-gray-200">
                    <span className="text-xs font-bold text-gray-600">Total sesiones elegibles en el trimestre</span>
                    <span className="text-sm font-bold text-violet-700 text-right tabular-nums">{totalEligibleQ}</span>
                  </div>
                </>
              ) : (
                <>
                  {(() => {
                    const flat = eligibleProcesses
                      .flatMap(p => p.eligibleDetails.map((s, idx) => ({ ...s, patientName: p.patientName, processId: p.processId, eligibleIdx: idx + 1 })))
                      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                    return flat.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-2">No hay sesiones elegibles este trimestre.</p>
                    ) : flat.map(s => (
                      <div key={s.id} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-violet-300 shrink-0" />
                        <span className="text-xs text-gray-500">
                          {formatSessionDate(s.date)} — <span className="font-medium">{s.patientName}</span>
                          <span className="text-gray-400 ml-1">(sesión elegible #{s.eligibleIdx})</span>
                        </span>
                      </div>
                    ));
                  })()}
                </>
              )}
            </div>
          )}

          <Divider />

          {/* Collapsible: procesos sin umbral para $1.000 y $2.000 */}
          {!dataLoading && notEligible10Processes.length > 0 && (
            <div className="border border-gray-200 rounded-2xl overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenNotEligible10Tab3(b => !b)}
                className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-gray-600">Procesos sin umbral para los bonos de $1.000 y $2.000</span>
                  <span className="text-xs text-gray-400">
                    {notEligible10Processes.length} proceso{notEligible10Processes.length !== 1 ? 's' : ''} · sin sesiones elegibles aún — desde la 10ª sesión empieza a contar
                  </span>
                </div>
                <span className="text-xs text-gray-400 ml-2 shrink-0">{openNotEligible10Tab3 ? '▲' : '▼'}</span>
              </button>
              {openNotEligible10Tab3 && (
                <div className="px-5 py-3 bg-white flex flex-col gap-3">
                  <ViewToggle viewKey="not-eligible-10-tab3" />
                  {getViewMode('not-eligible-10-tab3') === 'process' ? (
                    notEligible10Processes.map(p => {
                      const remaining = 10 - p.totalSessions;
                      const isExpanded = expandedNE10.has(p.processId);
                      const isOutsideExpanded = expandedNE10Outside.has(p.processId);
                      const outsideSessions = p.allTimeDetails.filter(
                        s => !p.quarterDetails.find(q => q.id === s.id),
                      );
                      return (
                        <div key={p.processId} className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="px-4 py-3 flex items-start justify-between gap-4">
                            <button
                              type="button"
                              onClick={() => navigate(`/patients?q=${encodeURIComponent(p.patientName)}`)}
                              className="text-sm font-medium text-[#2D2049] hover:text-violet-700 hover:underline underline-offset-2 text-left transition-colors truncate min-w-0"
                            >
                              {p.patientName}
                            </button>
                            <div className="flex flex-col items-end shrink-0 gap-0.5">
                              <button
                                type="button"
                                onClick={() => setExpandedNE10(prev => {
                                  const next = new Set(prev);
                                  isExpanded ? next.delete(p.processId) : next.add(p.processId);
                                  return next;
                                })}
                                className="text-xs text-gray-600 font-medium flex items-center gap-1 hover:text-violet-700 transition-colors"
                              >
                                {p.totalSessions} sesión{p.totalSessions !== 1 ? 'es' : ''} acumuladas {isExpanded ? '▲' : '▼'}
                              </button>
                              <span className="text-xs text-gray-400">
                                {remaining <= 0
                                  ? 'Umbral alcanzado — la próxima ya clasifica para los bonos de $1.000 y $2.000'
                                  : `Necesita ${remaining} sesión${remaining !== 1 ? 'es' : ''} más para cruzar el umbral — desde la 10ª serán elegibles`}
                              </span>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="px-4 pb-3 pt-1 flex flex-col gap-2 border-t border-gray-100">
                              {/* Sesiones este trimestre — siempre abierto */}
                              <div className="flex flex-col gap-1">
                                <p className="text-xs font-semibold text-gray-500">Sesiones este trimestre</p>
                                {p.quarterDetails.length === 0 ? (
                                  <p className="text-xs text-gray-400 pl-1">Sin sesiones este trimestre.</p>
                                ) : (
                                  p.quarterDetails
                                    .slice()
                                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                    .map(s => {
                                      const globalIdx = p.allTimeDetails.findIndex(x => x.id === s.id) + 1;
                                      return (
                                        <div key={s.id} className="flex items-center gap-2">
                                          <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                                          <span className="text-xs text-gray-500">
                                            {formatSessionDate(s.date)}
                                            <span className="text-gray-400 ml-1">· sesión #{globalIdx} acumulada</span>
                                          </span>
                                        </div>
                                      );
                                    })
                                )}
                              </div>
                              {/* Sesiones fuera del trimestre — colapsable, cerrado por default */}
                              {outsideSessions.length > 0 && (
                                <div className="flex flex-col gap-1">
                                  <button
                                    type="button"
                                    onClick={() => setExpandedNE10Outside(prev => {
                                      const next = new Set(prev);
                                      isOutsideExpanded ? next.delete(p.processId) : next.add(p.processId);
                                      return next;
                                    })}
                                    className="text-xs text-gray-400 flex items-center gap-1 hover:text-gray-600 transition-colors"
                                  >
                                    Sesiones fuera del trimestre ({outsideSessions.length}) {isOutsideExpanded ? '▲' : '▼'}
                                  </button>
                                  {isOutsideExpanded && outsideSessions
                                    .slice()
                                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                    .map(s => {
                                      const globalIdx = p.allTimeDetails.findIndex(x => x.id === s.id) + 1;
                                      return (
                                        <div key={s.id} className="flex items-center gap-2">
                                          <span className="w-1.5 h-1.5 rounded-full bg-gray-200 shrink-0" />
                                          <span className="text-xs text-gray-400">
                                            {formatSessionDate(s.date)}
                                            <span className="ml-1">· sesión #{globalIdx} acumulada</span>
                                          </span>
                                        </div>
                                      );
                                    })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex flex-col gap-1">
                      {notEligible10Processes
                        .flatMap(p => p.quarterDetails.map(s => ({ ...s, patientName: p.patientName })))
                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                        .map(s => (
                          <div key={s.id} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                            <span className="text-xs text-gray-500">
                              {formatSessionDate(s.date)} — <span className="font-medium">{s.patientName}</span>
                              <span className="text-gray-400 ml-1">(no elegible)</span>
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <Divider />
          <RepasemosConceptosBtn />
          <PsiPlusAssistantCard />
          <Divider />
        </>
      )}

      {/* ── Chatbot ─────────────────────────────────────────────────────────── */}
      {!isNueva && (
        <PsiPlusChatbot data={{
          totalBronzeQ,
          totalEligibleQ,
          bronzeAchieved,
          silverAchieved,
          goldAchieved,
          currentTier,
          quarter: quarter.label,
          eligibleProcessesCount: eligibleProcesses.length,
          bronze5ProcessesCount: bronze5Processes.length,
          allBronzeEligibleCount: allBronzeEligible.length,
          notEligible5Count: notEligible5Processes.length,
          notEligible10Count: notEligible10Processes.length,
          totalQuarterSessions,
          isNueva,
        }} open={showAssistant} onClose={() => setShowAssistant(false)} />
      )}

    </div>
  );
};
